/**
 * Kono internal: every email to the customer about this RFQ, in one place.
 *
 *  - Suggested emails the system wrote but did not send (the acknowledgment
 *    on submit, "part of your RFQ is ready", a follow-up after the quote):
 *    read as the customer would, edit, Send or Dismiss. Nothing reaches the
 *    customer until Send.
 *  - Sent emails, with what the customer did: delivered, opened (how many
 *    times, when), clicked the link, bounced.
 *  - The follow-up timings in force, and a way to ask for a follow-up now.
 */
import { useCallback, useEffect, useState } from "react";
import {
  CustomerFollowUpTiming,
  CustomerFollowUpTrigger,
  RfqCustomerEmail,
  dismissSuggestedCustomerEmail,
  fetchRfqCustomerEmails,
  sendSuggestedCustomerEmail,
  suggestCustomerFollowUpNow,
} from "./api";

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : "");

const KIND_LABEL: Record<string, string> = {
  acknowledgment: "Acknowledgment (we received your RFQ)",
  "partial-available": "Part of your RFQ is ready",
  "quote-ready": "Quote email",
  "rejected-items": "Items we cannot supply",
  "ask-more-time": "We need more time",
  "no-stock": "We could not source it",
  "partial-watch": "More items available",
  "better-options": "Better options arrived",
  "follow-up-no-open": "Follow-up: can I help?",
  "follow-up-after-click": "Follow-up: after they opened the quote",
  "follow-up-after-purchase": "Follow-up: thank you for your order",
  custom: "Email",
};

const TRIGGER_LABEL: Record<CustomerFollowUpTrigger, string> = {
  "no-open": "not opened / not clicked",
  "after-click": "clicked the link, nothing since",
  "after-purchase": "purchased",
};

// Link markup ([label](url)) and !!stress!! shown as the customer reads it.
const renderPlain = (body: string) =>
  body
    .replace(/<a [^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/g, "$2 ($1)")
    .split(/(!![^!\n][^\n]*?!!|\[[^\]\n]+\]\(https?:\/\/[^\s)]+\))/g)
    .map((part, i) => {
      const stressed = part.match(/^!!([^\n]*?)!!$/);
      if (stressed) return <strong key={i} className="underline">{stressed[1]}</strong>;
      const link = part.match(/^\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)$/);
      if (link) return <span key={i}>{link[1]} <span className="text-gray-500">({link[2]})</span></span>;
      return <span key={i}>{part}</span>;
    });

/** One line on what the customer did with a sent email. */
export const trackingSummary = (e: RfqCustomerEmail): { text: string; tone: "none" | "opened" | "clicked" | "bounced" } => {
  const t = e.tracking;
  if (t.bouncedAt) return { text: `Bounced ${when(t.bouncedAt)}${t.bounceReason ? ` (${t.bounceReason})` : ""}`, tone: "bounced" };
  const parts: string[] = [];
  if (t.clicks > 0) parts.push(`Link clicked ${t.clicks === 1 ? "once" : `${t.clicks} times`}, first ${when(t.firstClickedAt)}`);
  if (t.opens > 0) parts.push(`Opened ${t.opens === 1 ? "once" : `${t.opens} times`}, first ${when(t.firstOpenedAt)}`);
  if (!parts.length) {
    return { text: t.deliveredAt ? `Delivered ${when(t.deliveredAt)}, not opened yet` : "Sent, no open or click recorded yet", tone: "none" };
  }
  return { text: parts.join(". "), tone: t.clicks > 0 ? "clicked" : "opened" };
};

const TONE_CLASS: Record<ReturnType<typeof trackingSummary>["tone"], string> = {
  none: "bg-gray-100 text-gray-700",
  opened: "bg-blue-100 text-blue-800",
  clicked: "bg-green-100 text-green-800",
  bounced: "bg-red-100 text-red-800",
};

const SuggestedEmail = ({
  email,
  rfqId,
  onChanged,
  onError,
}: {
  email: RfqCustomerEmail;
  rfqId: string;
  onChanged: () => Promise<void>;
  onError: (m: string) => void;
}) => {
  const [subject, setSubject] = useState(email.subject);
  const [text, setText] = useState(email.text);
  const [extraCc, setExtraCc] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const extraCcList = extraCc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
  const badCc = extraCcList.filter((e) => !/^[^\s@<>]+@[^\s@<>]+\.[a-z]{2,}$/i.test(e));

  const send = async () => {
    if (badCc.length) {
      onError(`Cc: "${badCc[0]}" is not an email address.`);
      return;
    }
    const allCc = [...email.cc, ...extraCcList];
    if (!window.confirm(`Send this email to ${email.to}${allCc.length ? ` (cc ${allCc.join(", ")})` : ""}?`)) return;
    setBusy(true);
    try {
      await sendSuggestedCustomerEmail(rfqId, email.id, { subject, text, ...(extraCcList.length ? { cc: extraCcList.join(", ") } : {}) });
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not send the email.");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if (!window.confirm("Dismiss this suggested email? It will not be sent and will not be suggested again.")) return;
    setBusy(true);
    try {
      await dismissSuggestedCustomerEmail(rfqId, email.id);
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not dismiss the email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs">
          <span className="font-semibold text-[#00062e]">{KIND_LABEL[email.kind] || KIND_LABEL.custom}</span>
          <span className="ml-2 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900">suggested, not sent</span>
          <div className="mt-0.5 text-gray-600">
            Why: {email.reason}. Suggested {when(email.createdAt)}
            {email.suggestedBy && email.suggestedBy !== "system" ? ` by ${email.suggestedBy}` : ""}.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50" onClick={() => setEditing((v) => !v)}>
            {editing ? "Done editing" : "Edit"}
          </button>
          <button type="button" disabled={busy} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50" onClick={() => void dismiss()}>
            Dismiss
          </button>
          <button type="button" disabled={busy} className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50" onClick={() => void send()}>
            {busy ? "Working..." : "Send to customer"}
          </button>
        </div>
      </div>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-gray-700">
        <dt className="text-gray-500">From</dt>
        <dd>
          {email.from.name} &lt;{email.from.email}&gt;
        </dd>
        <dt className="text-gray-500">To</dt>
        <dd>{email.to}</dd>
        <dt className="text-gray-500">Cc</dt>
        <dd>
          {email.cc.length > 0 && <span>{email.cc.join(", ")}</span>}
          <input
            className={`mt-1 w-full rounded border px-2 py-1 text-xs ${badCc.length ? "border-red-400" : "border-gray-300"}`}
            placeholder="Add addresses to copy, separated by commas"
            value={extraCc}
            onChange={(e) => setExtraCc(e.target.value)}
          />
        </dd>
        <dt className="text-gray-500">Subject</dt>
        <dd>
          {editing ? (
            <input className="w-full rounded border border-gray-300 px-2 py-1 text-xs" value={subject} onChange={(e) => setSubject(e.target.value)} />
          ) : (
            <span className="font-medium text-gray-900">{subject}</span>
          )}
        </dd>
      </dl>
      {editing ? (
        <textarea className="mt-2 w-full rounded border border-gray-300 px-2 py-1 font-mono text-xs" rows={10} value={text} onChange={(e) => setText(e.target.value)} />
      ) : (
        <pre className="mt-2 whitespace-pre-wrap rounded border border-gray-200 bg-white px-3 py-2 font-sans text-xs text-gray-800">{renderPlain(text)}</pre>
      )}
    </div>
  );
};

const SentEmail = ({ email }: { email: RfqCustomerEmail }) => {
  const [open, setOpen] = useState(false);
  const s = trackingSummary(email);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <div>
          <span className="font-semibold text-[#00062e]">{KIND_LABEL[email.kind] || KIND_LABEL.custom}</span>
          <span className="ml-2 text-gray-600">
            sent {when(email.sentAt)} by {email.sentBy === "system" ? "the system" : email.sentBy}
            {email.edited ? " (edited before sending)" : ""}
          </span>
          <div className="mt-0.5 text-gray-700">
            <span className="font-medium">{email.subject}</span> to {email.to}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${TONE_CLASS[s.tone]}`}>{s.text}</span>
          <button type="button" className="rounded border border-gray-300 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Show email"}
          </button>
        </div>
      </div>
      {open && <pre className="mt-2 whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-3 py-2 font-sans text-xs text-gray-800">{renderPlain(email.text)}</pre>}
    </div>
  );
};

export const CustomerEmailsPanel = ({
  rfqId,
  refreshKey,
  onError,
  hideQuoteEmail = false,
}: {
  rfqId: string;
  refreshKey: number;
  onError: (message: string) => void;
  /** The quote block above owns the held quote email; do not list it twice. */
  hideQuoteEmail?: boolean;
}) => {
  const [emails, setEmails] = useState<RfqCustomerEmail[] | null>(null);
  const [timing, setTiming] = useState<CustomerFollowUpTiming[]>([]);
  const [automatic, setAutomatic] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetchRfqCustomerEmails(rfqId);
      setEmails(r.emails);
      setTiming(r.timing);
      setAutomatic(r.automaticSendingAllowed);
    } catch {
      setEmails(null);
    }
  }, [rfqId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (!emails) return null;

  const suggested = emails.filter((e) => e.status === "suggested" && !(hideQuoteEmail && e.kind === "quote-ready"));
  const sent = emails.filter((e) => e.status === "sent").sort((a, b) => (b.sentAt || "").localeCompare(a.sentAt || ""));
  const dismissed = emails.filter((e) => e.status === "dismissed");
  if (!suggested.length && !sent.length && !dismissed.length) return null;

  const suggestNow = async (trigger: CustomerFollowUpTrigger) => {
    setSuggesting(true);
    try {
      await suggestCustomerFollowUpNow(rfqId, trigger);
      await load();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not suggest a follow-up.");
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className={`mt-4 rounded-xl border p-4 text-sm ${suggested.length ? "border-amber-300 bg-amber-50/40" : "border-gray-200 bg-white"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-[#00062e]">
          Customer emails
          <span className="ml-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700">Kono internal</span>
          {suggested.length > 0 && (
            <span className="ml-2 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900">
              {suggested.length} waiting for you
            </span>
          )}
        </h3>
        <span className="text-xs text-gray-600">
          {automatic
            ? "Automatic customer emails are ON (CRM Configuration)."
            : "Nothing goes to the customer unless you press Send. Automatic customer emails are held (CRM Configuration > Customer RFQ emails)."}
        </span>
      </div>

      {suggested.length > 0 && (
        <div className="mt-3 space-y-3">
          {suggested.map((e) => (
            <SuggestedEmail key={e.id} email={e} rfqId={rfqId} onChanged={load} onError={onError} />
          ))}
        </div>
      )}

      {sent.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-gray-700">Sent ({sent.length})</div>
          <div className="mt-1 space-y-2">
            {sent.map((e) => (
              <SentEmail key={e.id} email={e} />
            ))}
          </div>
        </div>
      )}

      {sent.length > 0 && (
        <div className="mt-3 rounded border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700">
          <div className="font-medium">Follow-up suggestions</div>
          <ul className="mt-1 space-y-0.5">
            {timing.map((t) => (
              <li key={t.trigger}>
                {TRIGGER_LABEL[t.trigger]}: suggested after {t.delay} {t.unit}
                {t.source === "learned" ? " (learned from the team)" : ""}
                {t.learned.samples > 0 && t.source !== "learned"
                  ? ` - the team has sent this after ${Math.round(t.learned.avgDelay * 10) / 10} ${t.unit} on average (${t.learned.samples} time${t.learned.samples === 1 ? "" : "s"})`
                  : ""}
              </li>
            ))}
          </ul>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-gray-500">Suggest a follow-up now:</span>
            {(["no-open", "after-click", "after-purchase"] as CustomerFollowUpTrigger[]).map((trigger) => (
              <button
                key={trigger}
                type="button"
                disabled={suggesting}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => void suggestNow(trigger)}
              >
                {TRIGGER_LABEL[trigger]}
              </button>
            ))}
          </div>
        </div>
      )}

      {dismissed.length > 0 && (
        <div className="mt-3 text-xs text-gray-600">
          <button type="button" className="underline" onClick={() => setShowDismissed((v) => !v)}>
            {showDismissed ? "Hide" : "Show"} {dismissed.length} dismissed
          </button>
          {showDismissed && (
            <ul className="mt-1 space-y-0.5">
              {dismissed.map((e) => (
                <li key={e.id}>
                  {KIND_LABEL[e.kind] || KIND_LABEL.custom}: "{e.subject}" dismissed {when(e.dismissedAt)} by {e.dismissedBy}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
